import { UserManager, WebStorageStateStore } from 'oidc-client-ts';
import { runtimeConfig } from '../app/config';

/**
 * The route the hidden renewal iframe loads. It is a real registered redirect URI at Authentik and
 * must stay identical here, in the Authentik blueprint and in whatever proxies the portal.
 */
export const SILENT_CALLBACK_PATH = '/auth/silent-callback';

/** The route the interactive login returns to. */
export const LOGIN_CALLBACK_PATH = '/auth/callback';

/**
 * Builds the OIDC client, or nothing when there is no provider to talk to.
 *
 * Extracted from the provider because two entry points need the exact same settings: the running
 * application, and the renewal iframe that has to complete a response before React is mounted at
 * all. Two hand-kept copies of these settings would be two chances to configure a redirect URI the
 * provider does not recognise.
 */
export function createUserManager(): UserManager | null {
  if (runtimeConfig.demoMode || !runtimeConfig.oidc.authority) return null;
  return new UserManager({
    authority: runtimeConfig.oidc.authority,
    client_id: runtimeConfig.oidc.clientId,
    redirect_uri: `${window.location.origin}${LOGIN_CALLBACK_PATH}`,
    post_logout_redirect_uri: window.location.origin,
    // Without this, oidc-client-ts refuses the renewal it was told to perform automatically:
    // `automaticSilentRenew` falls back to signinSilent() whenever no refresh token was issued, and
    // signinSilent() throws "No silent_redirect_uri configured". The visible symptom is worse than
    // the error suggests - the session stays on screen while every access token request returns
    // undefined, so the UI looks signed in and every API call fails until a manual reload.
    silent_redirect_uri: `${window.location.origin}${SILENT_CALLBACK_PATH}`,
    response_type: 'code',
    scope: runtimeConfig.oidc.scope,
    userStore: new WebStorageStateStore({ store: window.sessionStorage }),
    automaticSilentRenew: true,
    // Session monitoring needs `check_session_iframe`, and this Authentik does not publish it:
    // measured 2026-09-18 against the provider's discovery document, where the key is absent.
    // Left at `true` the option only logs a warning and monitors nothing, which reads like a
    // working safeguard in the console.
    monitorSession: false,
  });
}

/**
 * Completes a renewal response inside the hidden iframe.
 *
 * Runs before anything is rendered. The iframe's only job is to hand the response back to the
 * window that opened it; mounting the portal here would boot a second copy of the application
 * inside an invisible frame, and that copy would start its own renewal timer.
 *
 * A failure is deliberately swallowed. oidc-client-ts reports the outcome to the parent through its
 * own channel, and this document is never seen by anyone.
 */
export async function completeSilentCallback(): Promise<void> {
  const manager = createUserManager();
  if (!manager) return;
  try {
    await manager.signinSilentCallback();
  } catch {
    // Reported to the opener by oidc-client-ts; nothing here can act on it.
  }
}
